
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useCart } from '../../../context/CartContext';
import { useQuickView } from '../../../context/QuickViewContext';
import { useWishlist } from '../../../context/WishlistContext';
import { shopKits } from '../../../mocks/shopProducts';

export default function ShopKits() {
  const { ref, isVisible } = useScrollAnimation();
  const { addToCart } = useCart();
  const { openQuickView } = useQuickView();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  const handleAdd = (kit: typeof shopKits[0]) => {
    addToCart({
      id: `kit-${kit.id}`,
      name: kit.name,
      price: kit.price,
      originalPrice: kit.originalPrice,
      image: kit.image,
      type: 'kit',
      description: kit.description,
    });
  };

  const toggleWishlist = (kit: typeof shopKits[0]) => {
    const wId = `kit-${kit.id}`;
    if (isInWishlist(wId)) {
      removeFromWishlist(wId);
    } else {
      addToWishlist({
        id: wId,
        name: kit.name,
        price: kit.price,
        originalPrice: kit.originalPrice,
        image: kit.image,
        type: 'kit',
        description: kit.description,
        badge: kit.badge,
        rating: kit.rating,
        reviews: kit.reviews,
      });
    }
  };

  const handleQuickView = (kit: typeof shopKits[0]) => {
    openQuickView({
      id: `kit-${kit.id}`,
      name: kit.name,
      description: kit.description,
      price: kit.price,
      originalPrice: kit.originalPrice,
      image: kit.image,
      type: 'kit',
      badge: kit.badge,
      details: [
        { label: 'Tools Included', value: `${kit.tools} pieces`, icon: 'ri-tools-line' },
        { label: 'Ingredients', value: `${kit.ingredients} items`, icon: 'ri-flask-line' },
        { label: 'Rating', value: `${kit.rating} (${kit.reviews} reviews)`, icon: 'ri-star-fill' },
        { label: 'Skill Level', value: kit.badge === 'Advanced' ? 'Advanced' : 'All Levels', icon: 'ri-bar-chart-line' },
      ],
      extras: ['Free shipping', 'Recipe cards included', '30-day guarantee'],
    });
  };

  return (
    <section id="shop-kits" className="py-24 bg-gradient-to-b from-stone-50 to-white scroll-mt-20">
      <div ref={ref} className="max-w-7xl mx-auto px-6">
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-400 to-pink-300 flex items-center justify-center shadow-lg shadow-rose-500/20">
                <i className="ri-box-3-line text-white"></i>
              </div>
              <span className="text-amber-600 text-xs font-semibold tracking-[0.2em] uppercase">Premium Equipment</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-normal text-stone-900">Curated Cocktail Kits</h2>
          </div>
          <p className="text-stone-500 max-w-md leading-relaxed text-sm">
            Everything you need to craft professional cocktails at home. Premium tools and ingredients in beautifully packaged kits.
          </p>
        </div>

        <div className={`grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-product-shop>
          {shopKits.map((kit) => (
            <div key={kit.id} className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-stone-100 hover:shadow-xl hover:border-amber-200/50 transition-all duration-500">
              <div className="relative h-56 bg-gradient-to-br from-amber-50 to-stone-50 p-5">
                <div className="absolute top-3 left-3 px-3 py-1 bg-stone-900 text-white text-[10px] font-semibold rounded-full">{kit.badge}</div>
                <div className="absolute top-3 right-3 flex flex-col gap-1.5">
                  <button
                    onClick={() => toggleWishlist(kit)}
                    className="w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
                  >
                    <i className={`text-base ${isInWishlist(`kit-${kit.id}`) ? 'ri-heart-fill text-rose-500' : 'ri-heart-line text-stone-300'}`}></i>
                  </button>
                </div>
                <img src={kit.image} alt={kit.name} className="w-full h-full object-contain hover:scale-110 transition-transform duration-500" />
                <button
                  onClick={() => handleQuickView(kit)}
                  className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full shadow-lg flex items-center gap-1.5 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 cursor-pointer hover:bg-stone-900 hover:text-white"
                >
                  <i className="ri-eye-line text-xs"></i>
                  <span className="text-[10px] font-semibold whitespace-nowrap">Quick View</span>
                </button>
              </div>

              <div className="p-5">
                <h3 className="text-base font-serif font-semibold text-stone-900 mb-1">{kit.name}</h3>
                <p className="text-stone-500 text-xs mb-3 leading-relaxed line-clamp-2">{kit.description}</p>

                <div className="flex items-center gap-3 text-xs text-stone-400 mb-3">
                  <span className="flex items-center gap-1">
                    <i className="ri-tools-line text-amber-500"></i>{kit.tools} tools
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-flask-line text-amber-500"></i>{kit.ingredients} items
                  </span>
                </div>

                <div className="flex items-center gap-1.5 mb-3">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-fill text-xs ${i < Math.floor(kit.rating) ? 'text-amber-400' : 'text-stone-200'}`}></i>
                    ))}
                  </div>
                  <span className="text-xs text-stone-500 font-medium">{kit.rating}</span>
                  <span className="text-xs text-stone-400">({kit.reviews})</span>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-stone-100">
                  <div>
                    <span className="text-xl font-display text-stone-900">${kit.price}</span>
                    <span className="text-stone-300 line-through text-xs ml-1.5">${kit.originalPrice}</span>
                  </div>
                  <button
                    onClick={() => handleAdd(kit)}
                    className="w-10 h-10 rounded-full bg-stone-900 text-white flex items-center justify-center hover:bg-gradient-to-r hover:from-amber-400 hover:to-yellow-300 hover:text-stone-900 transition-all cursor-pointer"
                  >
                    <i className="ri-shopping-cart-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
